import { useMemo, useState } from 'react';
import { transformUnit } from '@shared/misc/helpers/data';
import { EmpreinteCarboneTelephonesProps, UseEmpreinteCarboneTelephonesProps } from './type';

const BRAND_COLORS: Record<string, string> = {
  Autres: '#C4C4C4',
};
const SELECT_OPTIONS = [
  { label: '6 derniers mois', value: -6 },
  { label: '1 an', value: -12 },
  { label: '2 ans', value: -24 },
  { label: '3 ans', value: -36 },
];

const EMPTY_RESULT = {
  chartData: [{ label: '0 (0%)', percentage: 0, fill: '#C4C4C4' }],
  chartDataCO2: [{ label: '0 (0%)', percentage: 0, fill: '#C4C4C4' }],
  legends: [],
  total: 0,
  totalCO2: { value: '0', unit: '', index: 0 },
  brandTelephones: [],
};

const useData = (data: EmpreinteCarboneTelephonesProps['data']): UseEmpreinteCarboneTelephonesProps => {
  const [selectedPeriod, setSelectedPeriod] = useState(-12);
  const [popupData, setPopupData] =
    useState<{ total: number; type: string; list: { label: string; count: number }[] }>();

  const result = useMemo(() => {
    if (!data?.data?.details) return EMPTY_RESULT;

    const dateLimit = new Date();
    dateLimit.setMonth(dateLimit.getMonth() + selectedPeriod);
    const filtered = data.data.details.filter((item) => new Date(item.dateAchat) >= dateLimit);

    if (!filtered.length) return EMPTY_RESULT;

    // Grouper par marque
    const brands = filtered.reduce((acc, item) => {
      const brand = acc[item.marque] || { quantite: 0, empreinte: 0, telephones: [] };
      brand.quantite += item.quantite;
      brand.empreinte += item.totalEmpreinteCarbone;
      brand.telephones.push({ label: `${item.marque} ${item.modele}`, count: item.quantite });
      acc[item.marque] = brand;
      return acc;
    }, {} as Record<string, { quantite: number; empreinte: number; telephones: { label: string; count: number }[] }>);

    const totalQuantite = Object.values(brands).reduce((sum, b) => sum + b.quantite, 0);
    const totalEmpreinte = Object.values(brands).reduce((sum, b) => sum + b.empreinte, 0);
    const co2Unit = transformUnit(totalEmpreinte, 'co2eq');

    // Construire allBrands (top 3 par pourcentage + autres)
    const sortedBrands = Object.entries(brands).sort(([, a], [, b]) => b.quantite - a.quantite);
    const topBrands = sortedBrands.slice(0, 3);
    const otherBrands = sortedBrands.slice(3);

    // Assigner des couleurs aux top 3 marques
    const topBrandColors: Record<string, string> = {};
    const colors = ['#00263C', '#CC4E0A', '#109DB9'];
    topBrands.forEach(([name], index) => {
      topBrandColors[name] = colors[index];
    });

    // Construire le résultat final
    const allBrands = [
      ...topBrands,
      ...(otherBrands.length > 0
        ? [
            [
              'Autres',
              otherBrands.reduce(
                (acc, [, b]) => ({
                  quantite: acc.quantite + b.quantite,
                  empreinte: acc.empreinte + b.empreinte,
                  telephones: [...acc.telephones, ...b.telephones],
                }),
                { quantite: 0, empreinte: 0, telephones: [] as { label: string; count: number }[] },
              ),
            ] as [string, { quantite: number; empreinte: number; telephones: { label: string; count: number }[] }],
          ]
        : []),
    ] as [string, { quantite: number; empreinte: number; telephones: { label: string; count: number }[] }][];

    return {
      chartData: allBrands.map(([name, { quantite }]) => {
        const percentage = Math.round((quantite / totalQuantite) * 100);
        const color = topBrandColors[name] || BRAND_COLORS[name] || '#C4C4C4';
        return { label: `${quantite} (${percentage}%)`, percentage, fill: color };
      }),
      chartDataCO2: allBrands.map(([name, { empreinte }]) => {
        const percentage = Math.round((empreinte / totalEmpreinte) * 100);
        const { value, unit } = transformUnit(empreinte, 'co2eq', co2Unit.index);
        const color = topBrandColors[name] || BRAND_COLORS[name] || '#C4C4C4';
        return {
          label: `${value} ${unit} (${percentage}%)`,
          percentage,
          fill: color,
        };
      }),
      legends: allBrands.map(([name]) => {
        const color = topBrandColors[name] || BRAND_COLORS[name] || '#C4C4C4';
        return { name, symbol: { fill: color } };
      }),
      total: totalQuantite,
      totalCO2: co2Unit,
      brandTelephones: allBrands.map(([name, { telephones }]) => ({
        total: telephones.reduce((sum, { count }) => sum + count, 0),
        type: name.toLowerCase(),
        list: telephones,
      })),
    };
  }, [data?.data?.details, selectedPeriod]);

  return {
    selectOptions: SELECT_OPTIONS,
    ...result,
    selectedPeriod,
    onPeriodChange: setSelectedPeriod,
    popupData,
    openPopup: (index) => setPopupData(result.brandTelephones[index]),
    closePopup: () => setPopupData(undefined),
  };
};

export default useData;
